import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/layouts/RootLayout';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const MATERIALS = ['Cedar', 'Vinyl', 'Chain Link', 'Aluminum', 'Other'];
const HEIGHTS = ['4 ft', '5 ft', '6 ft', '8 ft', 'Custom'];

const newRun = id => ({ id, label: 'Run ' + id, length: '', height: '6 ft', notes: '' });

const FAQ = [
  ['How should I measure a fence job?', 'Break the layout into individual runs instead of recording only one total. Note each run length, height and important site conditions, then keep gates and removal quantities visible separately.'],
  ['Does this worksheet replace a site plan or survey?', 'No. It is a field-planning worksheet. Property lines, surveys, utilities, easements, permits and local requirements still need to be handled appropriately for the project.'],
  ['Can I send these measurements into the other PicketOps tools?', 'Yes. The worksheet can pass total footage, gate count, tear-out quantity and material into Material Takeoff, the Estimate Calculator and the Scope Builder.'],
  ['Are measurements saved to PicketOps?', 'No. This public worksheet stays in the open browser session unless you copy or download it. Saved customer and estimate records are created only inside the authenticated workspace.'],
];

export default function FenceMeasurementWorksheet() {
  const { isAuthenticated } = useAuth();
  const [project, setProject] = useState('');
  const [material, setMaterial] = useState('Cedar');
  const [runs, setRuns] = useState([newRun(1)]);
  const [gates, setGates] = useState('0');
  const [tearout, setTearout] = useState('0');
  const [siteNotes, setSiteNotes] = useState('');
  const [copied, setCopied] = useState(false);

  const totalFeet = useMemo(() => runs.reduce((sum, run) => {
    const value = Number(run.length);
    return sum + (Number.isFinite(value) && value > 0 ? value : 0);
  }, 0), [runs]);

  const completedRuns = runs.filter(run => Number.isFinite(Number(run.length)) && Number(run.length) > 0);
  const validGates = Number.isInteger(Number(gates)) && Number(gates) >= 0;
  const validTearout = Number.isFinite(Number(tearout)) && Number(tearout) >= 0;
  const ready = completedRuns.length > 0 && totalFeet > 0 && validGates && validTearout;

  const heightTotals = useMemo(() => {
    const grouped = {};
    completedRuns.forEach(run => {
      const key = run.height || 'Unspecified';
      grouped[key] = (grouped[key] || 0) + Number(run.length);
    });
    return Object.entries(grouped).sort((a,b)=>b[1]-a[1]);
  }, [completedRuns]);

  const updateRun = (id, patch) => {
    setRuns(current => current.map(run => run.id === id ? {...run, ...patch} : run));
    setCopied(false);
  };

  const addRun = () => {
    const nextId = runs.reduce((max, run) => Math.max(max, run.id), 0) + 1;
    setRuns(current => [...current, newRun(nextId)]);
  };

  const removeRun = id => {
    setRuns(current => current.length === 1 ? current : current.filter(run => run.id !== id));
  };

  const worksheetText = useMemo(() => {
    if (!ready) return '';
    const lines = [
      'PicketOps Fence Measurement Worksheet',
      project.trim() ? 'Project: ' + project.trim() : null,
      'Material: ' + material,
      '',
      'FENCE RUNS',
      ...completedRuns.flatMap((run, index) => [
        (index + 1) + '. ' + (run.label.trim() || 'Fence run') + ' — ' + Number(run.length).toLocaleString() + ' ft — ' + run.height,
        run.notes.trim() ? '   Notes: ' + run.notes.trim() : null,
      ]).filter(Boolean),
      '',
      'TOTAL FENCE LENGTH: ' + totalFeet.toLocaleString() + ' ft',
      'GATES: ' + Number(gates),
      'TEAR-OUT: ' + Number(tearout).toLocaleString() + ' ft',
      '',
      'FOOTAGE BY HEIGHT',
      ...heightTotals.map(([height, feet]) => '- ' + height + ': ' + feet.toLocaleString() + ' ft'),
      '',
      'SITE NOTES',
      siteNotes.trim() || 'No site notes added.',
      '',
      'Field worksheet only. Confirm property lines, utilities, layout, gate positions, measurements and project requirements before ordering or contracting.',
    ].filter(Boolean);
    return lines.join('\n');
  }, [ready, project, material, completedRuns, totalFeet, gates, tearout, heightTotals, siteNotes]);

  const copyWorksheet = async () => {
    if (!worksheetText) return;
    try {
      await navigator.clipboard.writeText(worksheetText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const clear = () => {
    setProject('');
    setMaterial('Cedar');
    setRuns([newRun(1)]);
    setGates('0');
    setTearout('0');
    setSiteNotes('');
    setCopied(false);
  };

  const workspaceTarget = isAuthenticated ? '/estimates' : '/contractor/signup';
  const query = ready
    ? '?feet=' + encodeURIComponent(totalFeet.toFixed(2))
      + '&gates=' + encodeURIComponent(gates)
      + '&tearout=' + encodeURIComponent(tearout)
      + '&material=' + encodeURIComponent(material)
    : '';

  return (
    <div className="picketops-public min-h-svh bg-background text-foreground">
      <header className="max-w-6xl mx-auto px-4 pt-20 pb-6 sm:pr-32">
        <Link to="/" aria-label="PicketOps home" className="inline-flex items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <span aria-hidden="true" className="flex size-10 items-center justify-center rounded-md bg-primary text-primary-foreground font-heading text-xl font-bold">P</span>
          <span><span className="block font-heading text-xl font-bold tracking-tight">PICKETOPS</span><span className="block text-xs text-muted-foreground">Revenue OS for fence contractors</span></span>
        </Link>
        <nav aria-label="Breadcrumb" className="mt-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:underline">Home</Link><span aria-hidden="true"> / </span>
          <Link to="/fence-contractor-tools" className="hover:underline">Free tools</Link><span aria-hidden="true"> / </span>
          <span aria-current="page">Fence measurement worksheet</span>
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-4 pb-16 space-y-10">
        <section className="picketops-hero overflow-hidden rounded-3xl border border-primary/20 shadow-(--shadow-md)">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr]">
            <div className="p-6 sm:p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Free field measurement worksheet · No signup required</p>
              <h1 className="mt-4 text-4xl sm:text-5xl font-heading font-bold tracking-tight leading-tight">Break a fence job into runs before the quote math starts</h1>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">Record each fence run separately, total the footage, keep heights and notes visible, then carry the measurements into material takeoff, pricing and scope building.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild><a href="#measurement-worksheet">Start measuring <ApperIcon name="ArrowDown" size={16}/></a></Button>
                <Button variant="outline" asChild><Link to="/fence-material-takeoff-calculator">Open material takeoff</Link></Button>
              </div>
            </div>
            <div className="border-t lg:border-t-0 lg:border-l border-border bg-card/60 p-6 sm:p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">A cleaner field handoff</p>
              <div className="mt-5 space-y-3">
                {[
                  ['Route','Separate runs instead of one mystery total'],
                  ['Ruler','Total footage automatically'],
                  ['PanelTop','Track height by run'],
                  ['NotebookPen','Keep site notes with the measurement'],
                ].map(([icon,label])=><div key={label} className="flex items-center gap-3 rounded-lg border border-border bg-background p-3 text-sm"><span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"><ApperIcon name={icon} size={16}/></span><span>{label}</span></div>)}
              </div>
            </div>
          </div>
        </section>

        <section id="measurement-worksheet" aria-labelledby="measurement-heading" className="space-y-6 scroll-mt-6">
          <Card>
            <CardContent className="p-5 sm:p-6 space-y-5">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div><p className="text-xs font-semibold uppercase tracking-wide text-primary">Project baseline</p><h2 id="measurement-heading" className="mt-2 text-2xl font-heading font-semibold">Capture the field measurements run by run.</h2></div>
                <Button type="button" variant="outline" onClick={addRun}><ApperIcon name="Plus" size={16}/>Add fence run</Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label htmlFor="measurement-project">Project label <span className="text-muted-foreground">(optional)</span></Label><Input id="measurement-project" value={project} onChange={event=>setProject(event.target.value)} placeholder="Smith backyard fence"/></div>
                <div className="space-y-2"><Label htmlFor="measurement-material">Primary material</Label><Select value={material} onValueChange={setMaterial}><SelectTrigger id="measurement-material"><SelectValue/></SelectTrigger><SelectContent>{MATERIALS.map(value=><SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent></Select></div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-3">
            {runs.map((run,index)=>(
              <Card key={run.id} className="overflow-hidden">
                <CardContent className="p-5">
                  <div className="grid gap-4 md:grid-cols-[1fr_0.7fr_0.7fr_1.3fr_auto] md:items-end">
                    <div className="space-y-2"><Label htmlFor={'run-label-'+run.id}>Run label</Label><Input id={'run-label-'+run.id} value={run.label} onChange={event=>updateRun(run.id,{label:event.target.value})} placeholder={'Run '+(index+1)}/></div>
                    <div className="space-y-2"><Label htmlFor={'run-length-'+run.id}>Length (ft)</Label><Input id={'run-length-'+run.id} type="number" min="0" step="0.01" inputMode="decimal" value={run.length} onChange={event=>updateRun(run.id,{length:event.target.value})} placeholder="48"/></div>
                    <div className="space-y-2"><Label htmlFor={'run-height-'+run.id}>Height</Label><Select value={run.height} onValueChange={value=>updateRun(run.id,{height:value})}><SelectTrigger id={'run-height-'+run.id}><SelectValue/></SelectTrigger><SelectContent>{HEIGHTS.map(value=><SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent></Select></div>
                    <div className="space-y-2"><Label htmlFor={'run-notes-'+run.id}>Run notes</Label><Input id={'run-notes-'+run.id} value={run.notes} onChange={event=>updateRun(run.id,{notes:event.target.value})} placeholder="Corner, grade change, gate opening, obstruction..."/></div>
                    <Button type="button" variant="ghost" size="icon" aria-label={'Remove '+(run.label||'fence run')} disabled={runs.length===1} onClick={()=>removeRun(run.id)}><ApperIcon name="Trash2" size={17}/></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
            <Card>
              <CardContent className="p-5 sm:p-6 space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label htmlFor="measurement-gates">Gate count</Label><Input id="measurement-gates" type="number" min="0" step="1" inputMode="numeric" value={gates} onChange={event=>setGates(event.target.value)} placeholder="2"/></div>
                  <div className="space-y-2"><Label htmlFor="measurement-tearout">Tear-out footage</Label><Input id="measurement-tearout" type="number" min="0" step="0.01" inputMode="decimal" value={tearout} onChange={event=>setTearout(event.target.value)} placeholder="80"/></div>
                </div>
                <div className="space-y-2"><Label htmlFor="measurement-site-notes">Site / access notes</Label><Textarea id="measurement-site-notes" rows={5} value={siteNotes} onChange={event=>setSiteNotes(event.target.value)} placeholder="Access, grade, utilities, landscaping, pets, retaining walls, customer questions..."/></div>
                {(!validGates||!validTearout)&&<p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">Gate count must be a whole nonnegative number and tear-out footage must be nonnegative.</p>}
                <Button type="button" variant="ghost" onClick={clear}><ApperIcon name="RotateCcw" size={16}/>Clear worksheet</Button>
              </CardContent>
            </Card>

            <section className="overflow-hidden rounded-2xl border border-primary/25 bg-card shadow-(--shadow-sm)">
              <div className="border-b border-border bg-primary/5 p-5 sm:p-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">Measurement summary</p>
                <p className="mt-2 text-5xl font-bold tabular-nums tracking-tight">{totalFeet.toLocaleString()} <span className="text-2xl font-semibold text-muted-foreground">ft</span></p>
                <p className="mt-2 text-sm text-muted-foreground">{completedRuns.length} measured {completedRuns.length===1?'run':'runs'} · {Number(gates)||0} {Number(gates)===1?'gate':'gates'} · {Number(tearout)||0} ft tear-out</p>
              </div>
              <div className="p-5 sm:p-6">
                {heightTotals.length ? <dl className="space-y-3">{heightTotals.map(([height,feet])=><div key={height} className="flex items-center justify-between gap-4 rounded-lg border border-border bg-background p-3"><dt className="text-sm font-medium">{height}</dt><dd className="text-sm font-bold tabular-nums">{feet.toLocaleString()} ft</dd></div>)}</dl> : <div className="flex min-h-40 items-center justify-center rounded-xl border border-dashed border-border bg-muted/20 p-6 text-center"><div><ApperIcon name="Ruler" size={26} className="mx-auto text-muted-foreground"/><p className="mt-3 text-sm font-semibold">Add a run length to build the summary.</p></div></div>}
              </div>
            </section>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button type="button" onClick={copyWorksheet} disabled={!ready}><ApperIcon name={copied?'Check':'Copy'} size={16}/>{copied?'Copied':'Copy measurements'}</Button>
            {ready?<Button variant="outline" asChild><a href={'data:text/plain;charset=utf-8,'+encodeURIComponent(worksheetText)} download="picketops-fence-measurements.txt"><ApperIcon name="Download" size={16}/>Download worksheet</a></Button>:<Button variant="outline" disabled><ApperIcon name="Download" size={16}/>Download worksheet</Button>}
            {ready&&<Button variant="outline" asChild><Link to={'/fence-material-takeoff-calculator'+query}><ApperIcon name="Boxes" size={16}/>Build material takeoff</Link></Button>}
            {ready&&<Button variant="outline" asChild><Link to={'/fence-labor-crew-planner?feet='+encodeURIComponent(totalFeet.toFixed(2))+'&job='+encodeURIComponent('Fence measurement worksheet')}><ApperIcon name="Users" size={16}/>Plan crew labor</Link></Button>}
            {ready&&<Button variant="outline" asChild><Link to={'/fence-estimate-calculator'+query}><ApperIcon name="Calculator" size={16}/>Price the job</Link></Button>}
            {ready&&<Button variant="outline" asChild><Link to={'/fence-estimate-template'+query}><ApperIcon name="FilePenLine" size={16}/>Build the scope</Link></Button>}
            <Button variant="outline" asChild><Link to={workspaceTarget}>{isAuthenticated?'Open saved estimates':'Create account'} <ApperIcon name="ArrowRight" size={16}/></Link></Button>
          </div>
        </section>

        <section aria-labelledby="measurement-faq-heading" className="max-w-4xl mx-auto">
          <h2 id="measurement-faq-heading" className="text-2xl font-heading font-semibold">Fence measurement worksheet questions</h2>
          <div className="mt-5 divide-y rounded-xl border border-border bg-card px-5">{FAQ.map(([question,answer])=><details key={question} className="py-4"><summary className="cursor-pointer rounded-md font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{question}</summary><p className="mt-3 text-sm leading-relaxed text-muted-foreground">{answer}</p></details>)}</div>
        </section>
      </main>
    </div>
  );
}

export const route={path:'/fence-measurement-worksheet',layout:'public'};
